<script setup>
import { ref, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import Api from 'dashboard/api/customers';

const props = defineProps({ contactId: { type: Number, default: null } });
const { t } = useI18n();
const route = useRoute();
const user = useMapGetter('getCurrentUser');
const enabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const allowed = computed(
  () =>
    enabled.value(route.params.accountId, 'customer_referrals') &&
    getUserPermissions(user.value, route.params.accountId).some(permission =>
      ['administrator', 'agent', 'contact_manage'].includes(permission)
    )
);
const customer = ref(null);
const { run, abort } = useAbortableRequest();
watch(
  [() => props.contactId, allowed, () => route.params.accountId],
  async () => {
    abort();
    customer.value = null;
    if (!allowed.value || !props.contactId) return;
    try {
      const response = await run(signal =>
        Api.get({ contact_id: props.contactId }, signal)
      );
      if (response) [customer.value] = response.data.payload;
    } catch {
      customer.value = null;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="customer" class="m-3 rounded-lg border border-n-weak p-3 text-sm">
    <RouterLink
      :to="{
        name: 'customers_show',
        params: { accountId: route.params.accountId, customerId: customer.id },
      }"
      class="font-medium text-n-blue-11"
      >{{ t('CUSTOMERS.VIEW_CUSTOMER') }}</RouterLink
    >
    <p v-if="customer.referral" class="mt-2">
      {{ t('CUSTOMERS.REFERRED_BY') }}: {{ customer.referral.referrer_name }}
    </p>
    <p class="mt-1 text-n-slate-11">
      {{ t(`CUSTOMERS.STATUSES.${customer.referral?.status || 'none'}`) }}
    </p>
    <p v-if="customer.expires_on" class="mt-1">
      {{ t('CUSTOMERS.EXPIRY') }}: {{ customer.expires_on }}
    </p>
  </div>
</template>
